import React, { useState } from 'react';
import { Button } from '../../components/buttons/Button';
import { Card } from '../../components/cards/Card';
import { Mail, Phone, MessageCircle, Clock, ChevronDown, ChevronUp, HelpCircle, Send } from 'lucide-react';

import { SEOHead } from '../../components/seo/SEOHead';
import { Breadcrumb } from '../../components/seo/Breadcrumb';

export const Contact: React.FC = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        role: 'Student',
        subject: '',
        message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [openFaq, setOpenFaq] = useState<number | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitError(null);

        try {
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData)
            });

            const result = await response.json();

            if (result.success) {
                setSubmitted(true);
                setFormData({ name: '', email: '', phone: '', role: 'Student', subject: '', message: '' });
            } else {
                setSubmitError(result.error || 'Failed to send message. Please try again.');
            }
        } catch (error) {
            setSubmitError('Network error. Please check your connection and try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const toggleFaq = (index: number) => {
        setOpenFaq(openFaq === index ? null : index);
    };

    const faqs = [
        { q: "How do I verify my account?", a: "To verify your account, go to your dashboard settings and upload the required identification documents. Admin approval takes 24-48 hours." },
        { q: "Is there a fee for students?", a: "No, Indigo Beans Rooms is completely free for students to browse and book accommodation." },
        { q: "How do I list a property?", a: "Register as a Landlord or Agent, choose a subscription plan, and click 'Add Property' in your dashboard." }
    ];

    return (
        <>
            <SEOHead
                title="Contact Us | IndigoBeans Rooms"
                description="Get in touch with IndigoBeans Rooms. We're here to help students find homes and landlords list properties. Support available 24/7."
                canonical="https://rooms.indigobeans.co.za/contact"
            />
            <div className="min-h-screen bg-slate-50 font-sans">
                {/* Breadcrumb */}
                <div className="bg-white border-b border-gray-100">
                    <div className="max-w-7xl mx-auto px-4 py-2">
                        <Breadcrumb items={[{ name: 'Contact Us', url: '/contact' }]} />
                    </div>
                </div>

                {/* 2. Page Header */}
                <div className="relative py-20 text-center text-white overflow-hidden">
                    <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=1950&q=80')] bg-cover bg-center"></div>
                    <div className="absolute inset-0 bg-[#2f5c96]/90 mix-blend-multiply"></div>
                    <div className="relative z-10 max-w-4xl mx-auto px-4">
                        <h1 className="text-4xl font-extrabold tracking-tight mb-4 text-white">Contact & Support</h1>
                        <p className="text-xl text-blue-100">We're here to help. Reach out to us with any questions or concerns.</p>
                    </div>
                </div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">

                        {/* 3. Contact Form Section */}
                        <div className="lg:col-span-2">
                            <Card className="p-8 shadow-lg border-t-4 border-t-[#2f5c96]">
                                {submitted ? (
                                    <div className="text-center py-16">
                                        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                            <Send className="w-8 h-8 text-green-600" />
                                        </div>
                                        <h3 className="text-2xl font-bold text-gray-900 mb-2">Message Sent!</h3>
                                        <p className="text-gray-600">Thank you for contacting us. We will get back to you shortly.</p>
                                        <Button variant="outline" className="mt-8" onClick={() => setSubmitted(false)}>Send Another Message</Button>
                                    </div>
                                ) : (
                                    <form onSubmit={handleSubmit} className="space-y-6">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                                                <input required type="text" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2f5c96] focus:border-transparent outline-none transition-all" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder="John Doe" />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                                                <input required type="email" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2f5c96] focus:border-transparent outline-none transition-all" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} placeholder="you@example.com" />
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number <span className="text-gray-400 font-normal">(Optional)</span></label>
                                                <input type="tel" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2f5c96] focus:border-transparent outline-none transition-all" value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} placeholder="+27..." />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">I am a...</label>
                                                <select className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2f5c96] focus:border-transparent outline-none transition-all bg-white" value={formData.role} onChange={e => setFormData({ ...formData, role: e.target.value })}>
                                                    <option>Student</option>
                                                    <option>Agent</option>
                                                    <option>Landlord</option>
                                                    <option>Other</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                                            <input required type="text" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2f5c96] focus:border-transparent outline-none transition-all" value={formData.subject} onChange={e => setFormData({ ...formData, subject: e.target.value })} placeholder="How can we help?" />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                                            <textarea required rows={5} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2f5c96] focus:border-transparent outline-none transition-all" value={formData.message} onChange={e => setFormData({ ...formData, message: e.target.value })} placeholder="Describe your issue or question..."></textarea>
                                        </div>
                                        {submitError && (
                                            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                                                {submitError}
                                            </div>
                                        )}
                                        <Button type="submit" className="w-full py-3 text-lg" disabled={isSubmitting}>
                                            {isSubmitting ? 'Sending...' : 'Send Message'}
                                        </Button>
                                    </form>
                                )}
                            </Card>
                        </div>

                        {/* Sidebar Info */}
                        <div className="space-y-8">

                            {/* 4. Alternative Contact Methods */}
                            <Card className="p-6">
                                <h3 className="text-lg font-bold text-gray-900 mb-6">Other Ways to Connect</h3>
                                <div className="space-y-4">
                                    <a href="mailto:rooms@indigobeans.co.za" className="flex items-center p-3 rounded-lg hover:bg-slate-50 transition-colors group">
                                        <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center text-[#2f5c96] group-hover:bg-[#2f5c96] group-hover:text-white transition-colors">
                                            <Mail className="w-5 h-5" />
                                        </div>
                                        <div className="ml-4">
                                            <p className="text-sm font-medium text-gray-900">Email Support</p>
                                            <p className="text-sm text-gray-500">rooms@indigobeans.co.za</p>
                                        </div>
                                    </a>
                                    <a href="tel:0108227138" className="flex items-center p-3 rounded-lg hover:bg-slate-50 transition-colors group">
                                        <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center text-[#2f5c96] group-hover:bg-[#2f5c96] group-hover:text-white transition-colors">
                                            <Phone className="w-5 h-5" />
                                        </div>
                                        <div className="ml-4">
                                            <p className="text-sm font-medium text-gray-900">Support Telephone</p>
                                            <p className="text-sm text-gray-500">010 822 7138</p>
                                        </div>
                                    </a>
                                    <a href="https://wa.me/27659038216" target="_blank" rel="noopener noreferrer" className="flex items-center p-3 rounded-lg hover:bg-slate-50 transition-colors group">
                                        <div className="w-10 h-10 bg-green-50 rounded-full flex items-center justify-center text-green-600 group-hover:bg-green-600 group-hover:text-white transition-colors">
                                            <MessageCircle className="w-5 h-5" />
                                        </div>
                                        <div className="ml-4">
                                            <p className="text-sm font-medium text-gray-900">WhatsApp</p>
                                            <p className="text-sm text-gray-500">065 903 8216</p>
                                        </div>
                                    </a>
                                </div>
                            </Card>

                            {/* 6. Support Hours */}
                            <div className="bg-slate-900 text-white rounded-xl p-6 shadow-md">
                                <div className="flex items-start gap-4">
                                    <Clock className="w-6 h-6 text-[#c19cca] mt-1" />
                                    <div>
                                        <h4 className="font-bold text-lg mb-1">Support Hours</h4>
                                        <p className="text-slate-300 text-sm mb-2">Monday – Friday</p>
                                        <p className="font-bold">09:00 AM – 05:00 PM</p>
                                        <div className="w-full h-px bg-slate-700 my-3"></div>
                                        <p className="text-xs text-slate-400">Response time within 24 hours.</p>
                                    </div>
                                </div>
                            </div>

                            {/* 5. FAQ Preview */}
                            <div>
                                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                                    <HelpCircle className="w-5 h-5 text-gray-400" /> Quick Answers
                                </h3>
                                <div className="space-y-3">
                                    {faqs.map((faq, idx) => (
                                        <div key={idx} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                                            <button onClick={() => toggleFaq(idx)} className="w-full flex items-center justify-between p-4 text-left text-sm font-medium text-gray-700 hover:bg-gray-50">
                                                {faq.q}
                                                {openFaq === idx ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                                            </button>
                                            {openFaq === idx && (
                                                <div className="p-4 bg-gray-50 text-xs text-gray-600 border-t border-gray-100 leading-relaxed">
                                                    {faq.a}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};